import { useState, useEffect } from 'react';
import { fetchSupportTicketDetail, fetchTicketFeedbacks, createTicketFeedback } from '@/service/api/supportTicket';
import { TicketAttachmentList } from './TicketAttachmentList';
import { TicketAttachmentUploader, UploadedAttachment } from './TicketAttachmentUploader';

export interface TicketDetailModalProps {
  ticket: Api.SupportTicket.TicketResponse | null;
  onClose: () => void;
  renderTicketStatus: (status?: string) => React.ReactNode;
  formatDate: (dateStr?: string) => string;
}

export function TicketDetailModal({ ticket, onClose, renderTicketStatus, formatDate }: TicketDetailModalProps) {
  const [currentTicket, setCurrentTicket] = useState<Api.SupportTicket.TicketResponse | null>(ticket);
  const [feedbacks, setFeedbacks] = useState<Api.SupportTicket.FeedbackResponse[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [newFeedbackContent, setNewFeedbackContent] = useState('');
  const [feedbackAttachments, setFeedbackAttachments] = useState<UploadedAttachment[]>([]);
  const [sendingFeedback, setSendingFeedback] = useState(false);

  useEffect(() => {
    if (!ticket) {
      setCurrentTicket(null);
      setFeedbacks([]);
      setFeedbackAttachments([]);
      return;
    }

    setCurrentTicket(ticket);
    setDetailLoading(true);
    setFeedbackAttachments([]);

    const loadDetail = async () => {
      try {
        const [detailRes, feedbackRes] = await Promise.all([
          fetchSupportTicketDetail(ticket.id || '').catch(() => ticket),
          fetchTicketFeedbacks(ticket.id || '').catch(() => ({ items: [] })),
        ]);
        if (detailRes) setCurrentTicket(detailRes);
        if (feedbackRes && Array.isArray(feedbackRes.items)) {
          setFeedbacks(feedbackRes.items);
        } else {
          setFeedbacks(detailRes?.feedbacks || []);
        }
      } catch (err) {
        console.error('获取工单详情失败', err);
      } finally {
        setDetailLoading(false);
      }
    };

    loadDetail();
  }, [ticket]);

  if (!ticket || !currentTicket) return null;

  const handleSendFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentTicket?.id || (!newFeedbackContent.trim() && feedbackAttachments.length === 0)) return;

    setSendingFeedback(true);
    try {
      const attachmentIds = feedbackAttachments.map((a) => a.id);
      const res = await createTicketFeedback(currentTicket.id, {
        content: newFeedbackContent.trim() || '（附件补充）',
        attachment_ids: attachmentIds.length > 0 ? attachmentIds : undefined,
        idempotency_key: `fb-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      });
      if (res) {
        setFeedbacks((prev) => [...prev, res]);
        setNewFeedbackContent('');
        setFeedbackAttachments([]);
      }
    } catch (err: any) {
      console.error('追加反馈失败', err);
      alert(err?.message || '发送反馈失败');
    } finally {
      setSendingFeedback(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col p-6 sm:p-8 shadow-2xl border border-slate-100 relative animate-[fadeIn_0.15s_ease-out]">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        {/* 顶部简要 */}
        <div className="pb-4 mb-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-mono text-slate-400">#{currentTicket.ticket_no || currentTicket.id}</span>
            {renderTicketStatus(currentTicket.status)}
          </div>
          <h3 className="text-lg font-bold text-slate-900">{currentTicket.description}</h3>
          <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500">
            <span>创建日期：{formatDate(currentTicket.created_at)}</span>
            {currentTicket.contact_phone && <span>手机：{currentTicket.contact_phone}</span>}
            {currentTicket.contact_email && <span>邮箱：{currentTicket.contact_email}</span>}
          </div>

          {/* 工单本身的描述附件 */}
          {currentTicket.attachments && currentTicket.attachments.length > 0 && (
            <TicketAttachmentList attachments={currentTicket.attachments} title="工单提交附件" />
          )}
        </div>

        {/* 对话反馈面板 */}
        <div className="flex-1 overflow-y-auto pr-1 space-y-4 mb-4">
          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">沟通与客服反馈</h4>

          {detailLoading ? (
            <div className="py-8 text-center text-slate-400">
              <i className="ri-loader-4-line text-2xl animate-spin text-emerald-600 block mb-1"></i>
              <span className="text-xs">加载反馈明细...</span>
            </div>
          ) : feedbacks.length === 0 ? (
            <div className="py-8 text-center bg-slate-50 rounded-xl text-slate-400 text-xs">
              暂无更多的沟通反馈，您可以随时在此留言追加说明。
            </div>
          ) : (
            feedbacks.map((fb, idx) => {
              const authorType = (fb.author_type || '').toLowerCase();
              const isUser = authorType === 'user';
              return (
                <div
                  key={fb.id || idx}
                  className={`p-3.5 rounded-xl text-xs sm:text-sm ${
                    isUser ? 'bg-slate-100 text-slate-800 mr-4' : 'bg-emerald-50/80 border border-emerald-100 text-emerald-950 ml-4'
                  }`}
                >
                  <div className="flex items-center justify-between text-xs text-slate-400 mb-1.5">
                    <span className="font-semibold">{isUser ? '我' : '官方客服 / 技术支持'}</span>
                    <span>{formatDate(fb.created_at)}</span>
                  </div>
                  <p className="whitespace-pre-wrap">{fb.content}</p>

                  {/* 反馈条目包含的附件 */}
                  {fb.attachments && fb.attachments.length > 0 && (
                    <TicketAttachmentList attachments={fb.attachments} title="" />
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* 追加反馈输入及附件上传 */}
        <form onSubmit={handleSendFeedback} className="pt-3 border-t border-slate-100 flex-shrink-0 space-y-3">
          <TicketAttachmentUploader
            value={feedbackAttachments}
            onChange={setFeedbackAttachments}
            maxCount={3}
            disabled={sendingFeedback}
          />
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="在此输入补充反馈信息..."
              value={newFeedbackContent}
              onChange={(e) => setNewFeedbackContent(e.target.value)}
              className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            />
            <button
              type="submit"
              disabled={sendingFeedback || (!newFeedbackContent.trim() && feedbackAttachments.length === 0)}
              className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-medium text-sm transition-colors cursor-pointer flex-shrink-0 flex items-center gap-1"
            >
              {sendingFeedback ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-fill"></i>}
              发送
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

